import {
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
} from '@aws-sdk/client-cognito-identity-provider'
import router from '@/router'

/**
 * AWS Cognito Authentication Plugin
 * Replaces Realm authentication with AWS Cognito User Pools
 */

// Cognito Configuration
const COGNITO_CONFIG = {
  region: process.env.VUE_APP_AWS_REGION,
  clientId: process.env.VUE_APP_AWS_COGNITO_CLIENT_ID,
}

// Cognito Client Instance
const cognitoClient = new CognitoIdentityProviderClient({
  region: COGNITO_CONFIG.region,
})

// LocalStorage Keys (changed from sessionStorage for cross-tab support)
const STORAGE_KEYS = { // TODO: migrate to security compliant token storage
  idToken: 'cognitoIdToken',
  accessToken: 'cognitoAccessToken',
  refreshToken: 'cognitoRefreshToken',
  tokenExpiry: 'cognitoTokenExpiry',
}

/**
 * Decode JWT token to extract payload
 * @param token - JWT token string
 * @returns Decoded token payload
 */
function decodeToken(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format')
    }

    const payload = JSON.parse(atob(parts[1]))
    return payload
  } catch (error) {
    console.error('Error decoding token:', error)
    return null
  }
}

// Clear auth storage
function clearAuthStorage() {
  localStorage.removeItem(STORAGE_KEYS.idToken)
  localStorage.removeItem(STORAGE_KEYS.accessToken)
  localStorage.removeItem(STORAGE_KEYS.refreshToken)
  localStorage.removeItem(STORAGE_KEYS.tokenExpiry)
  localStorage.removeItem('userData')
  localStorage.removeItem('clientData')
  localStorage.removeItem('userAbilityRules')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('guestId')
}

/**
 * Authenticate user with Cognito
 * @param credentials - User email and password
 * @returns Authentication tokens
 */
async function login(credentials) {
  try {
    console.log(`Authenticating user: ${credentials.email}`)

    // El pool es case-sensitive (Cognito no permite cambiarlo post-creación). El email
    // se normaliza a minúsculas para que cualquier combinación de casing que escriba el
    // usuario matchee con el valor que quedó guardado (el trigger también normaliza).
    const emailNormalizado = credentials.email.trim().toLowerCase()

    const params = {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: COGNITO_CONFIG.clientId,
      AuthParameters: {
        USERNAME: emailNormalizado,
        PASSWORD: credentials.password,
      },
    }

    const command = new InitiateAuthCommand(params)
    const response = await cognitoClient.send(command)

    if (!response.AuthenticationResult) {
      throw new Error('No authentication result returned from Cognito')
    }

    const { IdToken, AccessToken, RefreshToken, ExpiresIn } = response.AuthenticationResult

    if (!IdToken || !AccessToken || !RefreshToken) {
      throw new Error('Missing tokens in authentication result')
    }

    // Calculate token expiry time
    const expiryTime = Date.now() + (ExpiresIn || 3600) * 1000

    // Store tokens in sessionStorage
    localStorage.setItem(STORAGE_KEYS.idToken, IdToken)
    localStorage.setItem(STORAGE_KEYS.accessToken, AccessToken)
    localStorage.setItem(STORAGE_KEYS.refreshToken, RefreshToken)
    localStorage.setItem(STORAGE_KEYS.tokenExpiry, expiryTime.toString())

    console.log('Authentication successful')
    console.log(`Token expires in: ${ExpiresIn} seconds`)

    return {
      idToken: IdToken,
      accessToken: AccessToken,
      refreshToken: RefreshToken,
      expiresIn: ExpiresIn || 3600,
    }
  } catch (error) {
    console.error('Authentication error:', error)

    // Handle specific Cognito errors
    if (error.name === 'NotAuthorizedException') {
      throw new Error('Invalid username or password')
    } else if (error.name === 'UserNotFoundException') {
      throw new Error('User not found')
    } else if (error.name === 'InvalidParameterException') {
      console.error('Note: Make sure USER_PASSWORD_AUTH flow is enabled in Cognito User Pool')
      throw new Error('Invalid authentication parameters')
    }

    throw error
  }
}

async function forgotPassword(email) {
  try {
    const command = new ForgotPasswordCommand({ ClientId: COGNITO_CONFIG.clientId, Username: email.trim().toLowerCase() })
    const response = await cognitoClient.send(command)
    return { success: true, codeDeliveryDetails: response.CodeDeliveryDetails }
  } catch (error) {
    if (error.name === 'UserNotFoundException') throw new Error('User not found')
    if (error.name === 'LimitExceededException') throw new Error('Too many attempts. Please try again later.')
    if (error.name === 'InvalidParameterException') throw new Error('Invalid email format')
    throw error
  }
}

async function confirmForgotPassword(email, code, newPassword) {
  try {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: COGNITO_CONFIG.clientId,
      Username: email.trim().toLowerCase(),
      ConfirmationCode: code,
      Password: newPassword,
    })
    await cognitoClient.send(command)
    return { success: true }
  } catch (error) {
    if (error.name === 'CodeMismatchException') throw new Error('Invalid verification code')
    if (error.name === 'ExpiredCodeException') throw new Error('Verification code has expired')
    if (error.name === 'InvalidPasswordException') throw new Error('Password does not meet requirements')
    if (error.name === 'UserNotFoundException') throw new Error('User not found')
    throw error
  }
}

/**
 * Logout user and clear session
 * Cleans sessionStorage and localStorage
 */
async function logout() {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken)

    // Optional: Call Cognito GlobalSignOut (requires valid access token)
    if (accessToken) {
      try {
        const command = new GlobalSignOutCommand({
          AccessToken: accessToken,
        })
        await cognitoClient.send(command)
        console.log('Global sign out successful')
      } catch (error) {
        console.warn('Global sign out failed, continuing with local logout:', error)
      }
    }

    console.log('Logout successful')
  } catch (error) {
    console.error('Logout error:', error)
  } finally {
    clearAuthStorage()

    // Redirect to login page
    if (router.currentRoute.name !== 'auth-login') {
      router.push({ name: 'auth-login' })
    }
  }
}

/**
 * Get current authenticated user from idToken
 * @returns User claims from JWT token
 */
function getCurrentUser() {
  try {
    const idToken = localStorage.getItem(STORAGE_KEYS.idToken)

    if (!idToken) {
      return null
    }

    const decoded = decodeToken(idToken)

    if (!decoded) {
      return null
    }

    return {
      sub: decoded.sub,
      email: decoded.email,
      'cognito:username': decoded['cognito:username'] || decoded.email,
      'cognito:groups': decoded['cognito:groups'] || [],
      exp: decoded.exp,
    }
  } catch (error) {
    console.error('Error getting current user:', error)
    return null
  }
}

/**
 * Check if user is authenticated and token is valid
 * @returns true if authenticated with valid token
 */
function isAuthenticated() {
  const idToken = localStorage.getItem(STORAGE_KEYS.idToken)
  const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken)
  return !!idToken && !!refreshToken
}

/**
 * Refresh authentication tokens using refresh token
 * @returns New authentication tokens
 */
async function refreshAuthToken() {
  try {
    const currentRefreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken)

    if (!currentRefreshToken) {
      throw new Error('No refresh token available')
    }

    const params = {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: COGNITO_CONFIG.clientId,
      AuthParameters: {
        REFRESH_TOKEN: currentRefreshToken,
      },
    }

    const command = new InitiateAuthCommand(params)
    const response = await cognitoClient.send(command)

    if (!response.AuthenticationResult) {
      throw new Error('No authentication result returned from token refresh')
    }

    const { IdToken, AccessToken, ExpiresIn } = response.AuthenticationResult

    if (!IdToken || !AccessToken) {
      throw new Error('Missing tokens in refresh result')
    }

    // Calculate new token expiry time
    const expiryTime = Date.now() + (ExpiresIn || 3600) * 1000

    // Update tokens in sessionStorage
    localStorage.setItem(STORAGE_KEYS.idToken, IdToken)
    localStorage.setItem(STORAGE_KEYS.accessToken, AccessToken)
    localStorage.setItem(STORAGE_KEYS.tokenExpiry, expiryTime.toString())

    console.log('Token refresh successful')

    return {
      idToken: IdToken,
      accessToken: AccessToken,
      expiresIn: ExpiresIn || 3600,
    }
  } catch (error) {
    console.error('Token refresh error:', error)

    // If refresh fails, user needs to login again
    await logout()
    return null
  }
}

// Get valid token with automatic refresh if needed
let refreshPromise = null

async function getValidToken(type = 'accessToken') {
  const idToken = localStorage.getItem(STORAGE_KEYS.idToken)
  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken)
  const tokenExpiry = Number(localStorage.getItem(STORAGE_KEYS.tokenExpiry) || 0)
  const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken)

  if (!idToken || !refreshToken || !accessToken) {
    throw new Error('User is not authenticated')
  }

  // margen de seguridad: refrescar 60s antes
  const shouldRefresh = Date.now() >= (tokenExpiry - 60_000)

  // ✅ Si NO hace falta refresh → devolvés directo
  if (!shouldRefresh) {
    return type === 'idToken' ? idToken : accessToken
  }

  // 🔄 Evita múltiples refresh en paralelo
  if (!refreshPromise) {
    refreshPromise = refreshAuthToken()
      .catch((error) => {
        clearAuthStorage()
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  const refreshed = await refreshPromise

  if (!refreshed) {
    throw new Error('Token refresh failed')
  }

  return type === 'idToken'
    ? refreshed.idToken
    : refreshed.accessToken
}

/**
 * Get authorization headers for Lambda invocation
 * @returns Headers object with Authorization bearer token
 */
async function getAuthHeaders() {
  const validIdToken = await getValidToken('idToken')

  if (!validIdToken) {
    throw new Error('No authentication token available')
  }

  return {
    Authorization: `Bearer ${validIdToken}`,
  }
}

// Generate or retrieve guest ID for unauthenticated users
function getGuestId() {
  let guestId = localStorage.getItem('guestId')

  if (!guestId) {
    guestId = crypto.randomUUID()
    localStorage.setItem('guestId', guestId)
  }

  return guestId
}

// Get headers for API requests, including auth token if authenticated, or guest ID if not
async function getRequestHeaders() {
  if (isAuthenticated()) {
    return await getAuthHeaders()
  }

  return {
    'x-guest-id': getGuestId(),
  }
}

// ========================
// Export Composable
// ========================

export function useCognito() {
  return {
    login,
    logout,
    getCurrentUser,
    isAuthenticated,
    refreshAuthToken,
    getRequestHeaders,
    getValidToken,
    forgotPassword,
    confirmForgotPassword,
  }
}
